/**
 * DesignationWatermark Component
 *
 * Renders a semi-transparent diagonal text overlay for feature maturity designation.
 * Used for large UI sections (panels, page sections, full-page features, modals).
 *
 * Behavior per tier:
 *   coming_soon  - Watermark overlay blocks interaction (pointer-events: none on content)
 *   prototype    - Watermark overlay does NOT block interaction
 *   alpha        - Watermark overlay does NOT block interaction
 *   demo         - Watermark overlay does NOT block interaction
 *
 * Production features (no designation) render children without any overlay.
 *
 * @see https://gitlab.com/smart-assets.io/gitlab-profile/-/blob/master/docs/designs/feature-maturity-designation-system.md
 */
import React from "react";
import type { FeatureDesignation } from "../../types/designation";
import { DESIGNATION_STYLES, isInteractive } from "../../types/designation";

export interface DesignationWatermarkProps {
  /** The feature designation tier. Null/undefined = production (no watermark). */
  designation?: FeatureDesignation | null;
  /** Child content to render within the watermarked area */
  children: React.ReactNode;
  /** Additional CSS classes for the wrapper */
  className?: string;
}

/**
 * Opacity values per tier (design doc specification).
 * coming_soon: 15-20% opacity
 * prototype: 8-12% opacity
 * alpha: 8-12% opacity
 * demo: 6-10% opacity
 */
const WATERMARK_OPACITY: Record<FeatureDesignation, string> = {
  coming_soon: "opacity-[0.17]",
  prototype: "opacity-[0.10]",
  alpha: "opacity-[0.10]",
  demo: "opacity-[0.08]",
};

/**
 * Text color for watermark per tier (matches badge text colors).
 */
const WATERMARK_TEXT_COLOR: Record<FeatureDesignation, string> = {
  coming_soon: "text-sa-text-muted",
  prototype: "text-purple-500",
  alpha: "text-sa-warning",
  demo: "text-primary",
};

const DesignationWatermark: React.FC<DesignationWatermarkProps> = ({
  designation,
  children,
  className = "",
}) => {
  // Production features: no watermark, render children directly
  if (!designation) {
    return <>{children}</>;
  }

  const interactive = isInteractive(designation);
  const style = DESIGNATION_STYLES[designation];

  return (
    <div
      data-testid={`designation-watermark-${designation}`}
      className={`relative overflow-hidden ${className}`}
    >
      {/* Watermark overlay - positioned absolutely, non-interactive */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none select-none z-10"
        aria-hidden="true"
      >
        <span
          className={`
            text-6xl font-black uppercase tracking-[0.2em]
            -rotate-[30deg]
            whitespace-nowrap
            ${WATERMARK_OPACITY[designation]}
            ${WATERMARK_TEXT_COLOR[designation]}
          `}
        >
          {style.label}
        </span>
      </div>

      {/* Content area - pointer-events blocked for coming_soon only */}
      <div className={!interactive ? "pointer-events-none" : ""}>{children}</div>
    </div>
  );
};

export default DesignationWatermark;
